import {
  Directive, ElementRef, EventEmitter, Input, Output, Renderer2
} from '@angular/core';

export interface WsResizePayload {
  side: 'left' | 'right';
  deltaDays: number;   // на сколько дней изменили (с учётом клампа)
  nativeElement: HTMLElement;
  startWidth: number;
  startLeft: number;
}

@Directive({ selector: '[wsResizable]' })
export class ResizableDirective {
  @Input() dayWidth = 36;
  @Input() disabled = false;
  @Output() wsResizeEnd = new EventEmitter<WsResizePayload>();

  private side: 'left' | 'right' | null = null;
  private startX = 0;
  private baseLeft = 0;   // px на момент старта ресайза
  private baseWidth = 0;  // px на момент старта ресайза
  private startWidth = 0;
  private startLeft = 0;

  constructor(private el: ElementRef<HTMLElement>, private r: Renderer2) {
    const left = this.r.createElement('div');
    const right = this.r.createElement('div');
    this.r.addClass(left, 'ws-resizer'); this.r.addClass(left, 'left');
    this.r.addClass(right, 'ws-resizer'); this.r.addClass(right, 'right');
    this.r.appendChild(this.el.nativeElement, left);
    this.r.appendChild(this.el.nativeElement, right);

    left.addEventListener('mousedown', (e: MouseEvent) => this.start(e, 'left'));
    right.addEventListener('mousedown', (e: MouseEvent) => this.start(e, 'right'));
  }

  getLeftFromTransform(el: HTMLElement): string {
    const transform = el.style.transform;
    const match = /translate(?:3d|X)?\(([-\d.]+)px/.exec(transform);
    return match ? `${parseFloat(match[1])}px` : '0px';
  }

  private start(e: MouseEvent, side: 'left' | 'right') {
    if (this.disabled) {
      return;
    }

    e.preventDefault(); e.stopPropagation();
    this.side = side;
    this.startX = e.clientX;
    this.startWidth = parseFloat(this.el.nativeElement.style.width);
    this.startLeft = parseFloat(this.el.nativeElement.style.left);

    const computed = window.getComputedStyle(this.el.nativeElement);

    // let left = this.getLeftFromTransform(this.el.nativeElement);
    let left = this.el.nativeElement.style.left;

    const leftPx = parseFloat(left || computed.left || '0');
    const widthPx = parseFloat(computed.width);

    this.baseLeft = isNaN(leftPx) ? 0 : leftPx;
    this.baseWidth = isNaN(widthPx) ? 0 : widthPx;

    document.addEventListener('mousemove', this.onMove);
    document.addEventListener('mouseup', this.onUp);
  }

  private onMove = (e: MouseEvent) => {
    if (!this.side) return;

    // размеры контейнера col-days (границы для левой/правой кромки события)
    const colDays = this.el.nativeElement.closest('.col-days') as HTMLElement;
    const rect = colDays.getBoundingClientRect();
    const totalWidth = rect.width;

    // смещение курсора от старта
    const dx = e.clientX - this.startX;

    // квант в днях
    let days = Math.round(dx / this.dayWidth);

    // запретим выход за границы: считаем л/п кромки в днях относительно col-days
    if (this.side === 'right') {
      // новая правая кромка в пикселях:
      let newRightPx = this.baseLeft + this.baseWidth + days * this.dayWidth;
      // clamp к правой границе col-days
      newRightPx = Math.min(newRightPx, totalWidth);
      // пересчитанный days после клампа:
      days = Math.round((newRightPx - (this.baseLeft + this.baseWidth)) / this.dayWidth);

      // новая ширина (мин. 1 день)
      const newWidthPx = Math.max(this.dayWidth, this.baseWidth + days * this.dayWidth);
      this.el.nativeElement.style.width = newWidthPx + 'px';
      // left не меняем
    } else if (this.side === 'left') {
      // новая левая кромка в пикселях:
      let newLeftPx = this.baseLeft + days * this.dayWidth;
      // clamp к левой (0) и к правой (не вылезти правой кромкой)
      const maxLeftPx = Math.max(0, (this.baseLeft + this.baseWidth) - this.dayWidth); // минимум 1 день ширины
      newLeftPx = Math.max(0, Math.min(newLeftPx, maxLeftPx));
      // newLeftPx = Math.min(newLeftPx, maxLeftPx);

      // пересчитать days после клампа:
      days = Math.round((newLeftPx - this.baseLeft) / this.dayWidth);

      const newWidthPx = Math.max(this.dayWidth, this.baseWidth - days * this.dayWidth);
      this.el.nativeElement.style.left = newLeftPx + 'px';
      this.el.nativeElement.style.width = newWidthPx + 'px';
    }
  };

  private onUp = (e: MouseEvent) => {
    if (!this.side) return;

    const colDays = this.el.nativeElement.closest('.col-days') as HTMLElement;
    const rect = colDays.getBoundingClientRect();
    const totalWidth = rect.width;

    const dx = e.clientX - this.startX;
    let days = Math.round(dx / this.dayWidth);

    if (this.side === 'right') {
      let newRightPx = this.baseLeft + this.baseWidth + days * this.dayWidth;
      newRightPx = Math.min(newRightPx, totalWidth);
      days = Math.round((newRightPx - (this.baseLeft + this.baseWidth)) / this.dayWidth);
    } else {
      let newLeftPx = this.baseLeft + days * this.dayWidth;
      const maxLeftPx = Math.max(0, (this.baseLeft + this.baseWidth) - this.dayWidth);
      newLeftPx = Math.max(0, Math.min(newLeftPx, maxLeftPx));
      days = Math.round((newLeftPx - this.baseLeft) / this.dayWidth);
    }

    // сообщаем итоговое изменение (с учётом клампа)
    this.wsResizeEnd.emit({ side: this.side, deltaDays: days, nativeElement: this.el.nativeElement, startWidth: this.startWidth, startLeft: this.startLeft });

    // чистим временные стили — дальше рисует Angular через [ngStyle]
    // this.el.nativeElement.style.left = '';
    // this.el.nativeElement.style.width = '';

    document.removeEventListener('mousemove', this.onMove);
    document.removeEventListener('mouseup', this.onUp);
    this.side = null;
  };
}
